import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import SpinnerPopup from "@/components/spinner-popup";
import {
  Trophy,
  TrendingUp,
  Target,
  Award,
  Star,
  Gift,
  Users,
  ArrowRight,
  ArrowLeft,
  Play,
  Clock,
  Zap,
  Hash,
  Eye,
  Bell,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatSADateTime, formatSADate } from "@shared/timezone";

export default function MobileDashboard() {
  const { toast } = useToast();
  const { user, loading } = useAuth();
  const [location, setLocation] = useLocation();
  const [showSpinnerPopup, setShowSpinnerPopup] = useState(false);
  const [hasShownPopupThisSession, setHasShownPopupThisSession] = useState(false);

  // PERFORMANCE: Single optimized dashboard query
  const { data: dashboard, isLoading: dashboardLoading } = useQuery<any>({
    queryKey: ["/api/user/dashboard-optimized"],
    staleTime: 30 * 1000, // Cache for 30 seconds
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Fetch live challenges separately (not included in dashboard-optimized)
  const { data: challengesData } = useQuery<any>({
    queryKey: ["/api/challenges"],
    staleTime: 30 * 1000,
  });

  // Fetch spinner settings and eligibility
  const { data: spinnerSettings } = useQuery<any>({
    queryKey: ['/api/spinner/settings'],
  });

  const { data: canSpin } = useQuery<any>({
    queryKey: ['/api/spinner/can-spin'],
    enabled: spinnerSettings?.enabled,
  });

  // Show spinner popup when user can spin (only once per session)
  useEffect(() => {
    if (spinnerSettings?.enabled && canSpin && !showSpinnerPopup && !hasShownPopupThisSession) {
      const timer = setTimeout(() => {
        setShowSpinnerPopup(true);
        setHasShownPopupThisSession(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [spinnerSettings?.enabled, canSpin, showSpinnerPopup, hasShownPopupThisSession]);

  // Show loading while authentication is being determined
  if (loading || dashboardLoading) {
    return <MobileDashboardSkeleton />;
  }

  // Dashboard requires authentication - redirect to auth if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Sign In Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please sign in to access your dashboard.
          </p>
          <Link href="/auth">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Sign In to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const competitions = dashboard?.liveCompetitions || [];
  const stats = dashboard?.stats || {};
  const entryHistory = dashboard?.entryHistory || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="text-white" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="px-4 pt-12 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Welcome back!</h1>
              <p className="text-blue-100 text-sm">
                {user?.firstName || user?.email?.split("@")[0] || "User"}
              </p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Trophy className="h-6 w-6" />
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              className="bg-white/20 rounded-lg p-3 text-center active:scale-[0.98] transition-transform"
              onClick={() => setLocation("/m/challenges")}
            >
              <div className="text-lg font-bold">{challengesData?.length || 0}</div>
              <div className="text-xs text-blue-100">Challenges</div>
            </button>
            <button
              type="button"
              className="bg-white/20 rounded-lg p-3 text-center active:scale-[0.98] transition-transform"
              onClick={() => setLocation("/m/competitions")}
            >
              <div className="text-lg font-bold" data-testid="text-active-competitions">
                {stats.liveCompetitions || 0}
              </div>
              <div className="text-xs text-blue-100">Active Comps</div>
            </button>
            <button
              type="button"
              className="bg-white/20 rounded-lg p-3 text-center active:scale-[0.98] transition-transform"
              onClick={() => setLocation("/m/friends")}
            >
              <div className="text-lg font-bold" data-testid="text-friends-count">
                {dashboard?.friendsCount || 0}
              </div>
              <div className="text-xs text-blue-100">Friends</div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6 space-y-6">

        {/* Active Competitions */}
        {competitions.length > 0 && (
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-lg flex items-center">
                <Trophy className="h-5 w-5 mr-2 text-yellow-600" />
                Active Competitions
              </CardTitle>
              <Link href="/m/competitions">
                <Button variant="ghost" size="sm" data-testid="link-view-all-competitions">
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {competitions.slice(0, 3).map((competition: any) => (
                <Link 
                  key={competition.competitionId || competition.id} 
                  href={`/m/competition/${competition.competitionId || competition.id}`}
                  className="block"
                >
                  <div 
                    className="border rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    data-testid={`card-competition-${competition.competitionId || competition.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-sm line-clamp-2 flex-1 pr-2">
                        {competition.competitionTitle || competition.title}
                      </h3>
                      <Badge variant={competition.competitionStatus === 'active' ? "default" : "secondary"} className="text-xs">
                        {competition.competitionStatus === 'active' ? "Active" : "Ended"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                      <span className="flex items-center">
                        <Gift className="h-3 w-3 mr-1" />
                        {competition.competitionPrize1 || competition.prize1 || competition.prize || "Prize"}
                      </span>
                      <span className="flex items-center">
                        <Hash className="h-3 w-3 mr-1" />
                        {competition.totalEntries || 0} entries
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Live Challenges */}
        {challengesData && challengesData.length > 0 && (
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-lg flex items-center">
                <Target className="h-5 w-5 mr-2 text-purple-600" />
                Live Challenges
              </CardTitle>
              <Link href="/m/challenges">
                <Button variant="ghost" size="sm" data-testid="link-view-all-challenges">
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {challengesData.slice(0, 2).map((challenge: any) => (
                <Link 
                  key={challenge.id} 
                  href={`/m/challenges/${challenge.id}`}
                  className="block"
                >
                  <div 
                    className="border rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    data-testid={`card-challenge-${challenge.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-sm line-clamp-2 flex-1 pr-2">
                        {challenge.name}
                      </h3>
                      <Badge variant="default" className="text-xs bg-purple-600">
                        {challenge.status === 'live' ? 'Live' : challenge.status}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                        <span>Target: {challenge.userProgress?.progressCount || 0}/{challenge.targetValue}</span>
                        <span className="flex items-center">
                          <Users className="h-3 w-3 mr-1" />
                          {challenge.totalParticipants || 0}
                        </span>
                      </div>
                      {challenge.userProgress && (
                        <Progress 
                          value={Math.min((challenge.userProgress.progressCount / challenge.targetValue) * 100, 100)} 
                          className="h-2"
                        />
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {competitions.length === 0 && (!challengesData || challengesData.length === 0) && (
          <Card>
            <CardContent className="text-center py-12">
              <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No Active Competitions
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Check back later for new competitions and challenges!
              </p>
              <Link href="/m/competitions">
                <Button data-testid="button-browse-competitions">
                  Browse Competitions
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions - Moved to Bottom */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <Zap className="h-5 w-5 mr-2 text-blue-600" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Link href="/m/competitions">
                <Button 
                  variant="outline" 
                  className="w-full h-16 flex-col gap-1"
                  data-testid="button-competitions"
                >
                  <Trophy className="h-5 w-5" />
                  <span className="text-xs">Competitions</span>
                </Button>
              </Link>
              <Link href="/m/challenges">
                <Button 
                  variant="outline" 
                  className="w-full h-16 flex-col gap-1"
                  data-testid="button-challenges"
                >
                  <Target className="h-5 w-5" />
                  <span className="text-xs">Challenges</span>
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Link href="/m/winners">
                <Button 
                  variant="outline" 
                  className="w-full h-16 flex-col gap-1"
                  data-testid="button-winners"
                >
                  <Award className="h-5 w-5" />
                  <span className="text-xs">Winners</span>
                </Button>
              </Link>
              <Link href="/m/friends">
                <Button 
                  variant="outline" 
                  className="w-full h-16 flex-col gap-1"
                  data-testid="button-friends"
                >
                  <Users className="h-5 w-5" />
                  <span className="text-xs">Friends</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Spinner Popup */}
      <SpinnerPopup 
        isOpen={showSpinnerPopup} 
        onClose={() => setShowSpinnerPopup(false)} 
      />
    </div>
  );
}

function MobileDashboardSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Header Skeleton */}
      <div className="text-white px-4 pt-12 pb-6" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-white/20" />
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg bg-white/20" />
            ))}
          </div>
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="px-4 py-6 space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

    </div>
  );
}
